import { clearTokens } from '@/services/authTokens';
import { clearCachedToken } from '@/services/api';
import { queryClient } from '@/config/queryClient';

let isHandlingAuthError = false; // Prevent race conditions

/**
 * Handle authentication errors by clearing invalid sessions
 * Only triggers on 401 with INVALID_JWT code to avoid clearing valid sessions on other errors
 */
interface AuthError {
  status?: number;
  code?: string;
}

export async function handleAuthError(error: unknown): Promise<void> {
  // Guard against multiple simultaneous calls
  if (isHandlingAuthError) return;

  try {
    isHandlingAuthError = true;

    // Only handle 401 with INVALID_JWT or NO_TOKEN code
    // Don't clear on 403 (permission denied), 500 (server error), or network errors
    const authError = error as AuthError;
    if (authError?.status === 401 && (authError?.code === 'INVALID_JWT' || authError?.code === 'NO_TOKEN')) {
      // Clear all tokens (localStorage + memory cache)
      clearTokens();
      clearCachedToken();
      localStorage.removeItem('tenant_config');
      localStorage.removeItem('tenant_config_time');

      // Clear React Query cache to prevent stale data when switching users
      queryClient.clear();

      // Redirect to login with session expired flag, preserving intended destination
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        const intendedUrl = currentPath + window.location.search + window.location.hash;
        const params = new URLSearchParams({ session_expired: 'true' });
        if (intendedUrl !== '/') {
          params.set('redirect', intendedUrl);
        }
        window.location.href = `/login?${params.toString()}`;
      }
    }
  } catch (err) {
    console.error('Error in handleAuthError:', err);
  } finally {
    isHandlingAuthError = false;
  }
}
